import React from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Image,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { useVendors } from "../../hooks/useVendors";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";

export function VendorShowcase() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation();
  const {
    data: vendors,
    isLoading,
    error,
  } = useVendors({
    featured: true,
    limit: 3,
  });

  if (isLoading || error || !vendors?.length) return null;

  const renderVendorCard = ({ item: vendor }) => (
    <TouchableOpacity
      style={styles.vendorCard}
      onPress={() => navigation.navigate("Vendor", { id: vendor.id })}
    >
      {/* Banner Image */}
      {vendor.banner_image ? (
        <Image
          source={{ uri: vendor.banner_image }}
          style={styles.bannerImage}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.bannerImage, styles.bannerPlaceholder]}>
          <Feather name="image" size={24} color={colors.textLight} />
        </View>
      )}

      <View style={styles.cardContent}>
        {/* Logo Image */}
        {vendor.logo_image ? (
          <Image source={{ uri: vendor.logo_image }} style={styles.logoImage} />
        ) : (
          <View style={[styles.logoImage, styles.logoPlaceholder]}>
            <Feather name="shopping-bag" size={20} color={colors.textLight} />
          </View>
        )}

        <View style={styles.vendorInfo}>
          <Text style={styles.vendorName}>{vendor.store_name}</Text>
          <View style={styles.vendorMeta}>
            <View style={styles.rating}>
              <Feather name="star" size={14} color={colors.primary} />
              <Text style={styles.ratingText}>
                {vendor.rating?.toFixed(1) || "0.0"}
              </Text>
            </View>
            <View style={styles.location}>
              <Feather name="map-pin" size={14} color={colors.textLight} />
              <Text style={styles.locationText}>{vendor.borough}</Text>
            </View>
          </View>
          <Text style={styles.description} numberOfLines={2}>
            {vendor.description}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Featured Shops</Text>
        <TouchableOpacity
          style={styles.viewAll}
          onPress={() => navigation.navigate("Vendors")}
        >
          <Text style={styles.viewAllText}>View All</Text>
          <Feather name="arrow-right" size={16} color={colors.primary} />
        </TouchableOpacity>
      </View>

      <FlatList
        data={vendors}
        renderItem={renderVendorCard}
        keyExtractor={(item) => item.id}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      />
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      paddingVertical: spacing.xs,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: spacing.lg,
      marginBottom: spacing.md,
    },
    title: {
      fontSize: 22,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: -0.5,
    },
    viewAll: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
    },
    viewAllText: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.primary,
    },
    scrollContent: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
      gap: spacing.md,
    },
    vendorCard: {
      width: 280,
      backgroundColor: colors.surface,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: "hidden",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      marginRight: spacing.sm,
    },
    bannerImage: {
      width: "100%",
      height: 100,
      backgroundColor: colors.backgroundSecondary,
    },
    bannerPlaceholder: {
      alignItems: "center",
      justifyContent: "center",
    },
    cardContent: {
      padding: spacing.md,
    },
    logoImage: {
      width: 56,
      height: 56,
      borderRadius: 28,
      marginTop: -38,
      borderWidth: 3,
      borderColor: colors.surface,
      backgroundColor: colors.backgroundSecondary,
    },
    logoPlaceholder: {
      alignItems: "center",
      justifyContent: "center",
    },
    vendorInfo: {
      marginTop: spacing.xs,
    },
    vendorName: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 2,
    },
    vendorMeta: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.md,
      marginBottom: spacing.xs,
    },
    rating: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    ratingText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
    },
    location: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    locationText: {
      fontSize: 13,
      color: colors.textSecondary,
    },
    description: {
      fontSize: 13,
      color: colors.textSecondary,
      lineHeight: 18,
    },
  });
